/**
 * /settings/bots/:botId — one bot's page: its name, what people see about it, its
 * instructions, who gets it, and whether it's on for everyone. `new` starts a bot.
 */
import { useState, type FormEvent, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  Badge,
  Banner,
  BotAvatar,
  Button,
  Input,
  Select,
  Skeleton,
} from '@servicecompressionllc/sc-design-system';
import { Check } from '@servicecompressionllc/sc-design-system/icons';
import { AppShell } from '../../components/AppShell.js';
import { Breadcrumbs } from '../../components/Breadcrumbs.js';
import { Toggle } from '../../components/Toggle.js';
import { useMe } from '../../hooks/useMe.js';
import { scopeKindOf } from '../../lib/scopeKinds.js';
import type { Bot, Group, Scope } from '../../lib/types.js';
import { useGroupsListLogic } from '../groups/useGroupsLogic.js';
import { useScopesLogic } from '../scopes/useScopesLogic.js';
import {
  MAX_BOT_DESCRIPTION_CHARS,
  MAX_BOT_INSTRUCTIONS_CHARS,
  MAX_BOT_NAME_CHARS,
  useBotsLogic,
  type BotDraft,
} from './useBotsLogic.js';

const NEW_BOT = 'new';
const BLANK: BotDraft = {
  name: '',
  description: '',
  instructions: '',
  enabled: false,
  groupIds: [],
};
const LABEL = 'text-xs font-semibold uppercase tracking-wider text-ink-subtle';

/** "3 people · 1 turned it off", or why nobody has it yet. */
const reachLine = (bot: Bot): string => {
  if (bot.groupIds.length === 0) return 'Not given to anyone yet';
  const people = `${String(bot.people)} ${bot.people === 1 ? 'person' : 'people'}`;
  return bot.turnedOff > 0 ? `${people} · ${String(bot.turnedOff)} turned it off` : people;
};

const count = (n: number): string => n.toLocaleString('en-US');

/** A labelled field with its help line and character count underneath. */
const Field = ({
  id,
  label,
  help,
  length,
  max,
  children,
}: {
  id: string;
  label: string;
  help?: string;
  length: number;
  max: number;
  children: ReactNode;
}) => (
  <div className="flex flex-col gap-1">
    <label htmlFor={id} className={LABEL}>
      {label}
    </label>
    {children}
    <div className="flex gap-2 text-xs">
      <span className="flex-1 text-ink-subtle">{help}</span>
      <span
        className={`tabular-nums ${length > max ? 'font-semibold text-danger' : 'text-ink-subtle'}`}
      >
        {count(length)} / {count(max)}
      </span>
    </div>
  </div>
);

const peopleLine = (n: number): string => `${String(n)} ${n === 1 ? 'person' : 'people'}`;

/** The groups that get the bot: picked in one list, each shown with how many people it has. */
const WhoGetsIt = ({
  groups,
  groupIds,
  onChange,
  disabled,
}: {
  groups: Group[];
  groupIds: string[];
  onChange: (groupIds: string[]) => void;
  disabled: boolean;
}) => {
  const chosen = groupIds
    .map((id) => groups.find((g) => g.id === id))
    .filter((g): g is Group => g !== undefined);
  return (
    <div className="flex flex-col gap-1.5">
      <span className={LABEL}>Who gets it</span>
      <Select
        aria-label="Who gets it"
        multiple
        options={groups.map((g) => ({ value: g.id, label: g.name }))}
        value={groupIds}
        onChange={onChange}
        placeholder="Pick the groups that get it"
        emptyMessage="No groups match."
        disabled={disabled}
        className="w-full sm:w-72"
      />
      {chosen.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {chosen.map((g) => (
            <Badge key={g.id}>
              {g.name} · {peopleLine(g.memberCount)}
            </Badge>
          ))}
        </div>
      ) : (
        <span className="text-xs text-ink-subtle">Nobody gets it until you add a group.</span>
      )}
    </div>
  );
};

/**
 * Every data capability, lit where one of the bot's groups grants it. Read only: a bot
 * reaches what its person can, so changing this means changing the group.
 */
const AccessView = ({
  botName,
  groups,
  groupIds,
  scopes,
}: {
  botName: string;
  groups: Group[];
  groupIds: string[];
  scopes: Scope[];
}) => {
  const chosen = groups.filter((g) => groupIds.includes(g.id));
  const capabilities = scopes
    .filter((s) => scopeKindOf(s) === 'data' && s.audience !== 'app')
    .map((s) => ({ scope: s, from: chosen.filter((g) => g.scopes.includes(s.key)) }))
    .sort((a, b) => Number(b.from.length > 0) - Number(a.from.length > 0));
  if (capabilities.length === 0) return null;
  const only = chosen.length === 1 ? chosen[0] : undefined;
  return (
    <div className="flex flex-col gap-1.5">
      <span className={LABEL}>What {botName || 'this bot'} has access to</span>
      <ul className="grid grid-cols-1 gap-1.5 sm:grid-cols-2 md:grid-cols-3">
        {capabilities.map(({ scope, from }) => {
          const lit = from.length > 0;
          return (
            <li
              key={scope.key}
              title={
                lit
                  ? `${scope.displayName} · from ${from.map((g) => g.name).join(', ')}`
                  : `${scope.displayName} · not in its groups`
              }
              className={`flex h-8 min-w-0 items-center gap-2 rounded-md border px-2.5 text-[13px] font-medium ${
                lit
                  ? 'border-success-border bg-surface text-ink'
                  : 'border-outline text-ink-subtle opacity-55'
              }`}
            >
              {lit && <Check size={12} aria-hidden className="flex-none text-success" />}
              <span className="truncate">{scope.displayName}</span>
              {lit && <span className="sr-only">(has access)</span>}
            </li>
          );
        })}
      </ul>
      <span className="text-xs text-ink-subtle">
        Comes from the groups it&apos;s given to, so it can&apos;t see more than its person. To
        change it, edit{' '}
        {only ? (
          <Link to={`/settings/groups/${only.id}`} className="text-link hover:underline">
            the {only.name} group
          </Link>
        ) : (
          <Link to="/settings/groups" className="text-link hover:underline">
            its groups
          </Link>
        )}
        .
      </span>
    </div>
  );
};

const BotForm = ({
  bot,
  logic,
  groups,
  scopes,
}: {
  bot: Bot | null;
  logic: ReturnType<typeof useBotsLogic>;
  groups: Group[];
  scopes: Scope[];
}) => {
  const navigate = useNavigate();
  const saved: BotDraft = bot
    ? {
        name: bot.name,
        description: bot.description,
        instructions: bot.instructions,
        enabled: bot.enabled,
        groupIds: bot.groupIds,
      }
    : BLANK;
  const [draft, setDraft] = useState<BotDraft>(saved);
  const edit = (patch: Partial<BotDraft>): void => {
    logic.resetSave();
    setDraft((d) => ({ ...d, ...patch }));
  };

  const name = draft.name.trim();
  const description = draft.description.trim();
  const instructions = draft.instructions.trim();
  const tooLong =
    name.length > MAX_BOT_NAME_CHARS ||
    description.length > MAX_BOT_DESCRIPTION_CHARS ||
    instructions.length > MAX_BOT_INSTRUCTIONS_CHARS;
  const changed =
    name !== saved.name ||
    description !== saved.description ||
    instructions !== saved.instructions ||
    draft.enabled !== saved.enabled ||
    draft.groupIds.length !== saved.groupIds.length ||
    draft.groupIds.some((id) => !saved.groupIds.includes(id));
  const canSave = name.length > 0 && !tooLong && changed && !logic.isSaving;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!canSave) return;
    const body = { ...draft, name, description, instructions };
    try {
      const result = await logic.save(bot?.id ?? null, body);
      if (!bot) void navigate(`/settings/bots/${result.id}`, { replace: true });
    } catch {
      // Shown from logic.saveError.
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      aria-label={bot ? `Edit ${bot.name}` : 'New bot'}
      className="flex flex-col gap-4"
    >
      <div className="flex items-center gap-3.5">
        <BotAvatar size="lg" state={draft.enabled ? 'new' : 'idle'} />
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <h2 className="truncate text-2xl font-bold text-ink">{name || 'New bot'}</h2>
          {bot && <span className="text-sm text-ink-muted">{reachLine(bot)}</span>}
        </div>
        <div className="flex flex-col items-end gap-1">
          <div className="flex items-center gap-2 text-sm font-medium text-ink">
            <Toggle
              checked={draft.enabled}
              onChange={() => edit({ enabled: !draft.enabled })}
              label={`${name || 'This bot'} on`}
              disabled={logic.isSaving}
            />
            {draft.enabled ? 'On' : 'Off'}
          </div>
          <span className="hidden text-xs text-ink-subtle sm:block">
            When it&apos;s off, nobody sees it.
          </span>
        </div>
      </div>

      {logic.saveError && (
        <Banner variant="danger">
          <Banner.Body>
            <Banner.Title>{logic.saveError.message}</Banner.Title>
          </Banner.Body>
        </Banner>
      )}

      <section
        aria-label={`${name || 'Bot'} settings`}
        className="flex flex-col gap-4 rounded-lg border border-outline bg-surface-elevated p-4 sm:p-5"
      >
        <WhoGetsIt
          groups={groups}
          groupIds={draft.groupIds}
          onChange={(groupIds) => edit({ groupIds })}
          disabled={logic.isSaving}
        />
        <span aria-hidden className="border-t border-outline" />
        <Field id="bot-name" label="Name" length={name.length} max={MAX_BOT_NAME_CHARS}>
          <Input
            id="bot-name"
            value={draft.name}
            onChange={(e) => edit({ name: e.target.value })}
            placeholder="Mechanic Bot"
            autoComplete="off"
            disabled={logic.isSaving}
          />
        </Field>
        <Field
          id="bot-description"
          label="Description"
          help="Shown to people when they first open its thread. The bot itself never reads it."
          length={description.length}
          max={MAX_BOT_DESCRIPTION_CHARS}
        >
          <Input
            id="bot-description"
            value={draft.description}
            onChange={(e) => edit({ description: e.target.value })}
            placeholder="Keeps field mechanics posted on their units."
            autoComplete="off"
            disabled={logic.isSaving}
          />
        </Field>
        <Field
          id="bot-instructions"
          label="Instructions"
          help="What to focus on and how to sound. Your safety guardrails still come first."
          length={instructions.length}
          max={MAX_BOT_INSTRUCTIONS_CHARS}
        >
          <textarea
            id="bot-instructions"
            value={draft.instructions}
            onChange={(e) => edit({ instructions: e.target.value })}
            disabled={logic.isSaving}
            rows={7}
            placeholder="What this bot is for, what to watch for, and how to brief people."
            className="w-full rounded-md border border-outline-strong bg-surface p-3 text-sm leading-relaxed text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          />
        </Field>
        <span aria-hidden className="border-t border-outline" />
        <AccessView botName={name} groups={groups} groupIds={draft.groupIds} scopes={scopes} />
      </section>

      {/* Pinned to the bottom of the screen on a phone, so Save is always in reach. */}
      <div className="sticky bottom-0 -mx-4 flex gap-2 border-t border-outline bg-surface-elevated px-4 py-3 sm:static sm:mx-0 sm:justify-end sm:border-0 sm:bg-transparent sm:p-0">
        <Button
          type="button"
          variant="secondary"
          onClick={() => navigate('/settings/bots')}
          disabled={logic.isSaving}
          className="flex-1 sm:flex-none"
        >
          Cancel
        </Button>
        <Button type="submit" disabled={!canSave} className="flex-1 sm:flex-none">
          {logic.isSaving ? 'Saving…' : bot ? 'Save' : 'Create bot'}
        </Button>
      </div>
    </form>
  );
};

export const BotConfigPage = () => {
  const { botId = NEW_BOT } = useParams();
  const meQuery = useMe();
  const logic = useBotsLogic(meQuery.data?.tenant.id);
  const groupsList = useGroupsListLogic(meQuery.data?.tenant.id);
  const scopeCatalog = useScopesLogic();
  const bot = logic.bots.find((b) => b.id === botId) ?? null;
  const loading = meQuery.isLoading || logic.isLoading;
  const missing = botId !== NEW_BOT && !loading && !bot && !logic.error;

  return (
    <AppShell>
      {/* One centered column: a form this narrow looks lost against the wide admin page. */}
      <div className="mx-auto w-full max-w-[760px]">
        <Breadcrumbs
          trail={[
            { label: 'Bots', to: '/settings/bots' },
            {
              label: botId === NEW_BOT ? 'New bot' : (bot?.name ?? 'Bot'),
              to: `/settings/bots/${botId}`,
            },
          ]}
        />
        {logic.error && (
          <Banner variant="danger" className="mb-4">
            <Banner.Body>
              <Banner.Title>{logic.error.message}</Banner.Title>
            </Banner.Body>
          </Banner>
        )}
        {loading ? (
          <Skeleton className="h-96 w-full rounded-lg" />
        ) : missing ? (
          <p className="text-sm text-ink-muted">This bot doesn&apos;t exist, or it was deleted.</p>
        ) : (
          <BotForm
            key={bot?.id ?? NEW_BOT}
            bot={bot}
            logic={logic}
            groups={groupsList.data ?? []}
            scopes={scopeCatalog.scopes}
          />
        )}
      </div>
    </AppShell>
  );
};
